import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CLOUD_PROVIDERS, type CloudPod, type CloudPodLive, type Machine } from '@couch/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { errorMessage } from '../../api/client';
import { CLOUD_PODS_KEY, CLOUD_SPEND_KEY, cloudApi } from '../../api/cloud';
import { GuardNotes, spendLine } from '../account/CloudLimitPanel';
import { CloudCostWarning, ConsoleLink } from '../machines/CloudBadge';
import { MACHINES_KEY } from '../machines/MachineActions';
import { perHour } from '../machines/NewCloudMachineDialog';

const STATUS: Readonly<Record<string, string>> = Object.freeze({
  PROVISIONING: 'Starting', STARTING: 'Starting', RUNNING: 'Running', EXITED: 'Stopped', ERROR: 'Error — see RunPod\'s console', TERMINATED: 'Deleted at RunPod',
});
/** Google's instance states (doc 71): TERMINATED is a stopped VM there. */
const VM_STATUS: Readonly<Record<string, string>> = Object.freeze({
  PROVISIONING: 'Starting', STAGING: 'Starting', RUNNING: 'Running', STOPPING: 'Stopping', STOPPED: 'Stopped', TERMINATED: 'Stopped',
  SUSPENDING: 'Suspending', SUSPENDED: 'Suspended — resume it in Google\'s console', REPAIRING: 'Google is repairing it',
});

function liveLine(live: CloudPodLive, pod: CloudPod | undefined, noVolume: boolean): string {
  if (pod?.provider === 'gcp') {
    const cost = live.status === 'RUNNING' ? `Google bills it by the second (${pod.cloud === 'spot' ? 'Spot price' : 'regular price'})` : 'no GPU cost now — Google still bills the disk';
    return `${VM_STATUS[live.status] ?? live.status} · ${cost}`;
  }
  const idle = noVolume ? 'nothing is billed now' : 'no GPU cost now — RunPod still bills the volume';
  const cost = live.costPerHour > 0 ? `costs ${perHour(live.costPerHour)} now` : idle;
  return `${STATUS[live.status] ?? live.status} · ${cost}`;
}

function podFacts(pod: CloudPod, noVolume: boolean): string {
  if (pod.provider === 'gcp') return `${pod.gpuName} · ${pod.cloud === 'spot' ? 'Spot' : 'Standard'} · zone ${pod.dataCenterIds.join(', ')} · disk ${pod.volumeGb} GB`;
  return `${pod.gpuName} · ${noVolume ? 'no volume' : `volume ${pod.volumeGb} GB`} · datacenter ${pod.dataCenterIds.join(', ')}`;
}

type Note = { text: string; error: boolean } | null;

function usePodActions(machine: Machine, podId: string, vm: boolean, setNote: (note: Note) => void) {
  const provider = vm ? 'Google' : 'RunPod';
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const onError = (e: unknown) => { setNote({ text: errorMessage(e), error: true }); };
  const switchPod = useMutation({
    mutationFn: (action: 'start' | 'stop') => cloudApi.switchPod(podId, action),
    onSuccess: (res, action) => {
      queryClient.setQueryData(['cloud-pod-live', podId], res);
      setNote({ text: action === 'start' ? `Starting ${machine.name} — it shows up as Online in about 1–3 minutes.` : `${machine.name} is stopped. ${provider} still bills its ${vm ? 'disk' : 'volume'}.`, error: false });
      void queryClient.invalidateQueries({ queryKey: MACHINES_KEY });
    },
    onError,
  });
  const remove = useMutation({
    mutationFn: () => cloudApi.deletePod(podId),
    onSuccess: async () => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: MACHINES_KEY }), queryClient.invalidateQueries({ queryKey: CLOUD_PODS_KEY })]);
      void navigate('/');
    },
    onError,
  });
  const confirmDelete = () => {
    const where = vm ? 'at Google Cloud? Its disk' : 'at RunPod? Its volume';
    const lost = `Delete ${machine.name} ${where} goes with it: ComfyUI, the models and the full-size outputs — Couch keeps only thumbnails (and the originals in your storage, if connected), so download the originals you want first (Download original in the gallery). ${provider} stops billing it. This cannot be undone.`;
    if (window.confirm(lost)) remove.mutate();
  };
  return { switchPod, remove, confirmDelete };
}

/** A pod or VM Couch made, on its machine page (docs 59, 71): the provider's state, Start / Stop, the console, Delete. */
export function CloudPodPanel({ machine, podId }: { machine: Machine; podId: string }) {
  const [note, setNote] = useState<Note>(null);
  const pods = useQuery({ queryKey: CLOUD_PODS_KEY, queryFn: cloudApi.cloudPods });
  const live = useQuery({ queryKey: ['cloud-pod-live', podId], queryFn: () => cloudApi.livePod(podId), refetchInterval: 15_000, retry: false });
  const spend = useQuery({ queryKey: CLOUD_SPEND_KEY, queryFn: cloudApi.spend });
  const pod = pods.data?.pods.find((p) => p.id === podId);
  const vm = pod?.provider === 'gcp' || machine.hardware?.cloud?.provider === 'gcp';
  // Without a volume (doc 72) there is nothing to stop and start again: it is used, then deleted.
  const noVolume = pod?.provider === 'runpod' && pod.volumeGb === 0;
  const { switchPod, remove, confirmDelete } = usePodActions(machine, podId, vm, setNote);
  const actions = noVolume ? [] : live.data?.live.actions ?? [];
  const busy = switchPod.isPending || remove.isPending;
  return (
    <section className="panel cloud-pod" aria-labelledby="pod-title">
      <h2 id="pod-title">{CLOUD_PROVIDERS[vm ? 'gcp' : 'runpod'].label}</h2>
      {live.isPending && <p role="status">Asking {vm ? 'Google' : 'RunPod'}…</p>}
      {live.isError && <p role="alert" className="form-error">{errorMessage(live.error)}</p>}
      {live.data && <p className="cloud-line">{liveLine(live.data.live, pod, noVolume)}</p>}
      {pod && <p className="muted">{podFacts(pod, noVolume)}</p>}
      {pod?.cloud === 'spot' && <p className="muted">Spot: Google may stop this VM at any time when it needs the GPU back. Start brings it back once Google has an L4 free again.</p>}
      {noVolume && <p className="cloud-warning">No volume: download the pictures you want to keep (Download original), then delete the pod. If it stops itself when idle, everything on it is gone — delete it then.</p>}
      <div className="pod-actions">
        {actions.includes('start') && <button type="button" disabled={busy} onClick={() => { switchPod.mutate('start'); }}>Start</button>}
        {actions.includes('stop') && <button type="button" className="button-quiet" disabled={busy} onClick={() => { switchPod.mutate('stop'); }}>Stop</button>}
        {pod && <ConsoleLink href={pod.consoleUrl} provider={pod.provider} />}
        <button type="button" className="button-quiet" disabled={busy} onClick={confirmDelete}>{vm ? 'Delete VM' : 'Delete pod'}</button>
      </div>
      {note && <p role={note.error ? 'alert' : 'status'} className={note.error ? 'form-error' : 'success'}>{note.text}</p>}
      {spend.data && !vm && <p className="muted">{spendLine(spend.data.spend)} Limit: Account → Cloud spending limit.</p>}
      {vm && <p className="muted">No spending limit for Google Cloud — the VM stops itself when idle; check Google&apos;s billing.</p>}
      {spend.data && <GuardNotes spend={spend.data.spend} />}
      <CloudCostWarning />
    </section>
  );
}
